import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../utils/api";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import {
  formatMembershipProductDisplayName,
  resolveSignupBillingLabel,
} from "../../utils/membershipProductDisplayName";
import {
  productSupportsVehicleTypeSelection,
  productHasCcwVariantPricing,
  resolveCheckoutRrp,
  resolveCcwMonthlyPrice,
  formatMonthlyPrice,
} from "../../utils/selfSignupPricing";
import {
  isInIframe,
  redirectToStripeCheckout,
  useIframeAutoResize,
} from "../../utils/iframeEmbed";
import {
  appendSignupAttributionToPayload,
  captureSignupAttributionFromSearchParams,
} from "../../utils/signupAttribution";
import {
  isNzRegion,
  phoneValidationMessage,
  phoneValidationTest,
} from "../../utils/phoneValidation";

// Normalize phone from URL/query to local 0… format (AU +61 / NZ +64 by region)
function normalizePhoneNumber(phone) {
  if (!phone) return phone;

  let normalized = String(phone).trim();

  try {
    normalized = decodeURIComponent(normalized);
  } catch (e) {
    normalized = String(phone).trim();
  }

  normalized = normalized.replace(/\s+/g, "");

  if (isNzRegion) {
    if (normalized.startsWith("+64")) {
      normalized = "0" + normalized.substring(3);
    } else if (normalized.startsWith("64") && /^64\d{8,10}$/.test(normalized)) {
      // 64 + 8–10 digit NSN (e.g. 6421527525 → 021527525)
      normalized = "0" + normalized.substring(2);
    }
  } else if (normalized.startsWith("+61")) {
    normalized = "0" + normalized.substring(3);
  } else if (
    normalized.startsWith("61") &&
    normalized.length === 11 &&
    /^61\d{9}$/.test(normalized)
  ) {
    normalized = "0" + normalized.substring(2);
  }

  return normalized;
}

const SelfSignUpRegister = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [linkDetails, setLinkDetails] = useState(null);
  const [productInfo, setProductInfo] = useState(null); // Fallback product info
  const [resolvedLockInMonths, setResolvedLockInMonths] = useState(null);
  const [billingInterval, setBillingInterval] = useState(null);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);
  const [identityModalPayload, setIdentityModalPayload] = useState(null);
  const [pendingCheckoutPayload, setPendingCheckoutPayload] = useState(null);
  const [checkoutInProgress, setCheckoutInProgress] = useState(false);
  const checkoutInFlightRef = useRef(false);
  const directCheckoutStartedRef = useRef(false);

  // Extract key and token from URL
  const key = searchParams.get("key");
  const token = searchParams.get("token");

  // Extract customer details from search params (when key/token don't exist)
  const email = searchParams.get("email");
  // Handle phone from query (AU +61 / NZ +64) — decode and normalize
  const phoneParam = searchParams.get("phone");
  const phone = phoneParam ? normalizePhoneNumber(phoneParam) : null;
  const firstName = searchParams.get("firstName");
  const lastName = searchParams.get("lastName");
  const productId = searchParams.get("productId");
  const customerId = searchParams.get("customerId"); // For existing customer links from admin

  // Optional search params — ignored for pricing when the link is key/token locked
  const rrp = searchParams.get("rrp");
  const partnerId = searchParams.get("partnerId");
  const partnerUserId = searchParams.get("partnerUserId");
  const vehicleTypeFromUrlRaw = searchParams.get("vehicleType"); // sedan | wagon | 4wd
  const siteIdFromUrl = searchParams.get("siteId");
  const locationNameFromUrl = searchParams.get("locationName");
  const lockInPeriodMonthsFromUrl = searchParams.get("lockInPeriodMonths");
  const isLp = searchParams.get("isLp") === "true";

  useIframeAutoResize(isLp || isInIframe());

  const resolveActiveProductId = () =>
    productId ||
    linkDetails?.idProduct?._id ||
    linkDetails?.idProduct ||
    linkDetails?.idSubscription?._id ||
    linkDetails?.idSubscription ||
    null;

  const hasCcwSiteContext = Boolean(
    searchParams.get("booking") || siteIdFromUrl || locationNameFromUrl
  );

  const pricingLocked = Boolean(key && token);
  const lockedVehicleType = pricingLocked ? linkDetails?.vehicleType || null : null;
  const vehicleTypeFromUrl = lockedVehicleType || vehicleTypeFromUrlRaw;

  const resolveCheckoutRrpForVehicle = (vehicleType) => {
    if (pricingLocked && linkDetails?.rrp != null) {
      return Number(linkDetails.rrp);
    }
    return resolveCheckoutRrp({
      productId: resolveActiveProductId(),
      vehicleType,
      urlRrp: rrp || linkDetails?.rrp,
      productInfo,
      hasCcwSiteContext,
    });
  };

  const appendCcwContextToPayload = (payload) => {
    const bookingFromUrl = searchParams.get("booking");
    const webhookUrlFromUrl = searchParams.get("webhookUrl");
    if (bookingFromUrl) payload.bookingId = bookingFromUrl;
    if (webhookUrlFromUrl) payload.webhookUrl = webhookUrlFromUrl;
    if (siteIdFromUrl) payload.siteId = siteIdFromUrl;
    if (locationNameFromUrl) payload.locationName = locationNameFromUrl;
    if (!pricingLocked && lockInPeriodMonthsFromUrl) {
      payload.lockInPeriodMonths = lockInPeriodMonthsFromUrl;
    }
    return payload;
  };

  const appendTrackingToPayload = (payload) => {
    const linkDefaults = linkDetails
      ? {
          utm_source: linkDetails.utmSource,
          utm_medium: linkDetails.utmMedium,
          utm_campaign: linkDetails.utmCampaign,
          utm_content: linkDetails.utmContent,
        }
      : {};
    return appendSignupAttributionToPayload(payload, searchParams, linkDefaults);
  };

  useEffect(() => {
    captureSignupAttributionFromSearchParams(searchParams);
  }, [searchParams]);

  // Form State
  const [formData, setFormData] = useState({
    email: email || "",
    phone: phone || "",
    firstName: firstName || "",
    lastName: lastName || "",
    addressState: "",
    vehicleType: vehicleTypeFromUrlRaw || "", // sedan | wagon | 4wd - for invoicing (AU and NZ)
    vehicleRegistration: "",
    vinNumber: "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleModelYear: "",
    vehicleAge: "",
    agreeTerms: false
  });

  useEffect(() => {
    if (!lockedVehicleType) return;
    setFormData((prev) =>
      prev.vehicleType === lockedVehicleType
        ? prev
        : { ...prev, vehicleType: lockedVehicleType }
    );
  }, [lockedVehicleType]);

  // Keep the URL in sync with the selected vehicle type so the link reflects the
  // chosen variant (and its price) on refresh/share, and rrp tracks CCW variant pricing.
  // Only applies when the URL already carries a vehicleType param — links without one
  // are left untouched.
  const syncVehicleTypeToUrl = (vehicleType) => {
    if (pricingLocked) return;
    if (!searchParams.has("vehicleType")) return;

    const nextParams = new URLSearchParams(searchParams);
    if (vehicleType) {
      nextParams.set("vehicleType", vehicleType);
    } else {
      nextParams.delete("vehicleType");
    }

    // Derive rrp from the variant's monthly price so it tracks the selected vehicle type
    // even for standalone variant links (no CCW site context). Fall back to the standard
    // resolver when the product has no per-variant pricing.
    const variantMonthly = resolveCcwMonthlyPrice(
      resolveActiveProductId(),
      vehicleType,
      productInfo || getActiveProduct()
    );
    const resolvedRrp =
      variantMonthly != null
        ? variantMonthly * 12
        : resolveCheckoutRrpForVehicle(vehicleType);
    if (resolvedRrp != null) {
      nextParams.set("rrp", String(resolvedRrp));
    }
    setSearchParams(nextParams, { replace: true });
  };

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));

    if (name === "vehicleType") {
      syncVehicleTypeToUrl(value);
    }
  };

  const getActiveProduct = () =>
    productInfo ||
    linkDetails?.idSubscription ||
    linkDetails?.idProduct ||
    null;

  // Coffee Pass never collects a vehicle, even if the product was misconfigured with variant pricing.
  const isCoffeePass = getActiveProduct()?.vertical === "cafe";

  const showVehicleTypeSelector =
    !isCoffeePass &&
    productSupportsVehicleTypeSelection(
      resolveActiveProductId(),
      getActiveProduct()
    ) &&
    !lockedVehicleType;

  const withoutVehicleFields = (payload) => {
    if (!isCoffeePass) return payload;
    const {
      vehicleType,
      vehicleRegistration,
      vinNumber,
      vehicleMake,
      vehicleModel,
      vehicleModelYear,
      vehicleAge,
      ...rest
    } = payload;
    return rest;
  };

  const vehicleTypeLabels = {
    sedan: "Sedan / Hatch",
    wagon: "Wagon / SUV",
    "4wd": "4WD / 7 Seater",
  };

  // Validate Form
  const validateForm = () => {
    const requiredFields = ["email", "phone", "firstName", "lastName"];
    if (showVehicleTypeSelector) {
      requiredFields.push("vehicleType");
    }

    for (let field of requiredFields) {
      if (!formData[field]) {
        showErrorToast(`Please fill in all required fields.`);
        return false;
      }
    }

    if (!validatePhone(formData.phone)) {
      showErrorToast(phoneValidationMessage);
      return false;
    }

    if (!formData.agreeTerms) {
      showErrorToast("You must agree to the terms and conditions.");
      return false;
    }

    return true;
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (value) => {
    if (!value) return false;
    return phoneValidationTest(normalizePhoneNumber(String(value)));
  };

  /**
   * @returns {"success" | "conflict" | "error"}
   */
  const startStripeCheckout = async (payload, identityConflictAction) => {
    if (checkoutInFlightRef.current) {
      return "in_progress";
    }
    checkoutInFlightRef.current = true;
    setCheckoutInProgress(true);

    let outcome = "error";
    try {
      const body =
        identityConflictAction && ["merge", "create_new"].includes(identityConflictAction)
          ? { ...payload, identityConflictAction }
          : { ...payload };

      const response = await api("v1/selfsignup/stripe-checkout", "POST", body);

      if (response.status === 409 && response.data?.errorCode === "CUSTOMER_IDENTITY_CONFLICT") {
        setIdentityModalPayload(response.data);
        setPendingCheckoutPayload(payload);
        setIdentityModalOpen(true);
        outcome = "conflict";
        return outcome;
      }

      if (response.data?.errorCode === "RECENT_CANCELLED_SUBSCRIPTION") {
        const blockedMessage =
          response.data?.message ||
          "Sorry, we could not onboard you at this time. Someone from our team will reach out to you shortly to discuss your options, or to let you know why we can't onboard you right now.";
        setError(blockedMessage);
        showErrorToast(blockedMessage);
        return outcome;
      }

      if (response.status === 429 || response.data?.errorCode === "CHECKOUT_IN_PROGRESS") {
        showErrorToast(
          response.data?.message ||
            "Checkout is already in progress. Please use your existing checkout window."
        );
        return outcome;
      }

      if (response.status === 409 && response.data?.errorCode === "PAYMENT_PROCESSING") {
        showErrorToast(response.data?.message || "Your payment is already being processed.");
        return outcome;
      }

      if (response.data?.success) {
        showSuccessToast("Redirecting to checkout...");
        redirectToStripeCheckout(response.data.checkoutUrl);
        outcome = "success";
        return outcome;
      }

      showErrorToast(response.data?.message || "Signup failed.");
      return outcome;
    } finally {
      if (outcome !== "success") {
        checkoutInFlightRef.current = false;
        setCheckoutInProgress(false);
      }
    }
  };

  const resolveIdentityAndCheckout = async (identityConflictAction) => {
    if (!pendingCheckoutPayload) return;
    setIdentityModalOpen(false);
    setLoading(true);
    const payload = pendingCheckoutPayload;
    setPendingCheckoutPayload(null);
    setIdentityModalPayload(null);
    try {
      const result = await startStripeCheckout(payload, identityConflictAction);
      if (result !== "success") setLoading(false);
    } catch (err) {
      console.error("Checkout error:", err);
      showErrorToast("Error processing checkout.");
      setLoading(false);
    }
  };

  // Direct Checkout (when all details are present)
  const goToCheckoutDirectly = async (payload) => {
    const vt = isCoffeePass
      ? null
      : lockedVehicleType ||
        formData.vehicleType ||
        searchParams.get("vehicleType");
    const mergedPayload = withoutVehicleFields(
      appendTrackingToPayload(
        appendCcwContextToPayload({
          ...payload,
          ...(vt ? { vehicleType: vt } : {}),
        }),
      ),
    );
    if (!pricingLocked) {
      const resolvedRrp = resolveCheckoutRrpForVehicle(vt);
      if (resolvedRrp != null) {
        mergedPayload.rrp = resolvedRrp;
      }
    } else {
      delete mergedPayload.rrp;
      delete mergedPayload.lockInPeriodMonths;
    }

    console.log("Direct checkout payload:", mergedPayload);
    setLoading(true);

    try {
      const result = await startStripeCheckout(mergedPayload, null);
      if (result !== "success") setLoading(false);
    } catch (error) {
      console.error("Error processing direct checkout:", error);
      setLoading(false);
      showErrorToast("Error processing checkout.");
    }
  };

  // Submit Checkout Data
  const goToCheckout = async () => {
    if (checkoutInProgress || checkoutInFlightRef.current) return;
    if (!validateForm()) return;

    let payload = {};

    // Include customerId if provided (from admin-generated link)
    if (customerId) {
      payload.customerId = customerId;
    }

    // If we have linkDetails (from either flow), use the idSignUpLink
    if (linkDetails && linkDetails._id) {
      payload.idSignUpLink = linkDetails._id;
      payload = { ...payload, ...formData };
    } else if (key && token) {
      // Fallback to key if linkDetails not available but we have key/token
      payload.idSignUpLink = key;
      payload = { ...payload, ...formData };
    } else {
      // Use search params flow as fallback
      payload = {
        ...formData,
        productId: productId,
        rrp: rrp || null, // Default if not provided
        partnerId: partnerId || null, // Default if not provided
        partnerUserId: partnerUserId || null // Default if not provided
      };
    }

    // Vehicle type: locked link value, then form, then URL
    payload.vehicleType =
      lockedVehicleType || formData.vehicleType || vehicleTypeFromUrl || null;
    if (!pricingLocked) {
      const resolvedRrp = resolveCheckoutRrpForVehicle(payload.vehicleType);
      if (resolvedRrp != null) {
        payload.rrp = resolvedRrp;
      } else if (!payload.rrp && rrp) {
        payload.rrp = rrp;
      }
    } else {
      delete payload.rrp;
      delete payload.lockInPeriodMonths;
    }
    if (!payload.vehicleType) {
      console.warn("[P1 Frontend] ⚠️ No vehicleType (form or URL)");
    }

    // Include CCW context if provided in URL (booking, webhook, location)
    appendTrackingToPayload(appendCcwContextToPayload(payload));
    payload = withoutVehicleFields(payload);
    
    console.log("[P1 Frontend] Final payload being sent to backend:", {
      ...payload,
      // Don't log sensitive data, just show structure
      hasVehicleType: !!payload.vehicleType,
      vehicleType: payload.vehicleType
    });

    try {
      const result = await startStripeCheckout(payload, null);
      if (result === "conflict") {
        /* modal open */
      } else if (result !== "success") {
        /* toast already shown */
      }
    } catch (error) {
      console.error("Error processing checkout:", error);
      showErrorToast("Error processing checkout.");
    }
  };

  useEffect(() => {
    // Check if we have key and token OR productId (with or without customer details)
    const hasKeyAndToken = key && token;
    const hasProductId = productId;
    const hasCustomerDetails = email && phone && firstName && lastName && productId;

    if (!hasKeyAndToken && !hasProductId) {
      showErrorToast(
        "Invalid or missing required parameters. Please ensure you have either a valid signup link or a product ID."
      );
      setLoading(false);
      return;
    }

    const fetchSignupDetails = async () => {
      try {
        let response;

        if (hasKeyAndToken) {
          // Original flow with key and token
          response = await api(`v1/selfsignup/details?key=${key}&token=${token}`, "GET");
        } else {
          // Search params flow - send productId and any available customer details to get link details
          const params = new URLSearchParams({
            productId: productId,
            ...(email && { email: email }),
            ...(phone && { phone: phone }),
            ...(firstName && { firstName: firstName }),
            ...(lastName && { lastName: lastName }),
            ...(rrp && { rrp: rrp }),
            ...(partnerId && { partnerId: partnerId }),
            ...(partnerUserId && { partnerUserId: partnerUserId }),
            ...(siteIdFromUrl && { siteId: siteIdFromUrl }),
            ...(locationNameFromUrl && { locationName: locationNameFromUrl }),
            ...(lockInPeriodMonthsFromUrl && {
              lockInPeriodMonths: lockInPeriodMonthsFromUrl,
            }),
          });

          response = await api(`v1/selfsignup/details?${params.toString()}`, "GET");
        }

        if (response.data.success) {
          const signupLink = response.data.signupLink;
          const lockInFromApi = response.data.lockInPeriodMonths ?? null;
          const billingFromApi =
            response.data.billingInterval ??
            response.data.interval ??
            signupLink?.idSubscription?.interval ??
            signupLink?.idProduct?.interval ??
            null;
          setResolvedLockInMonths(
            lockInFromApi ??
              (pricingLocked
                ? null
                : lockInPeriodMonthsFromUrl
                  ? Number(lockInPeriodMonthsFromUrl)
                  : null)
          );
          setBillingInterval(billingFromApi);
          setLinkDetails({
            ...signupLink,
            pricingLocked: Boolean(
              response.data.pricingLocked ?? signupLink?.pricingLocked ?? pricingLocked
            ),
            interval: billingFromApi,
            lockInPeriodMonths:
              lockInFromApi ??
              (pricingLocked ? null : lockInPeriodMonthsFromUrl) ??
              null,
            vehicleType:
              response.data.vehicleType || signupLink?.vehicleType || null,
          });
          
          // Extract product info from multiple possible locations
          const product = response.data.product || signupLink?.idProduct || signupLink?.idSubscription;
          if (product) {
            setProductInfo(product);
            console.log("Product info set:", product.name);
          } else {
            console.warn("No product info found in response:", response.data);
          }
        } else {
          showErrorToast("Failed to fetch signup link details. Please make sure the sign up link is valid.");
          
          // Fallback: Try to fetch product info directly if signup link fails
          if (productId) {
            try {
              const productResponse = await api(`v1/product/${productId}`, "GET");
              if (productResponse.data.success && productResponse.data.data) {
                setProductInfo(productResponse.data.data);
                console.log("Fetched product info as fallback:", productResponse.data.data);
              }
            } catch (productErr) {
              console.error("Error fetching product info:", productErr);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching signup link:", err);
        showErrorToast("An error occurred while fetching signup details.");
        
        // Fallback: Try to fetch product info directly if signup link fails
        if (productId) {
          try {
            const productResponse = await api(`v1/product/${productId}`, "GET");
            if (productResponse.data.success && productResponse.data.data) {
              setProductInfo(productResponse.data.data);
              console.log("Fetched product info as fallback:", productResponse.data.data);
            }
          } catch (productErr) {
            console.error("Error fetching product info:", productErr);
          }
        }
      }
      setLoading(false);
    };

    fetchSignupDetails();
  }, []); // Empty dependency array - only run once on mount

  // Separate useEffect to handle direct checkout after linkDetails is set
  useEffect(() => {
    if (!linkDetails) return;

    // Get productId from URL params OR from linkDetails (if link was created with key/token)
    const finalProductId = productId || linkDetails?.idProduct?._id || linkDetails?.idProduct || linkDetails?.idSubscription?._id || linkDetails?.idSubscription;
    const hasCustomerDetails = email && phone && firstName && lastName && finalProductId;
    const finalPartnerId = partnerId || linkDetails?.idPartnerCompany?._id;
    const finalPartnerUserId = partnerUserId || linkDetails?.idUser?._id;
    const hasAllDetailsForDirectCheckout = hasCustomerDetails && finalPartnerId && finalPartnerUserId;

    console.log("Direct checkout check after API:", {
      hasCustomerDetails,
      finalPartnerId,
      finalPartnerUserId,
      hasAllDetailsForDirectCheckout,
      linkDetails
    });

    if (hasAllDetailsForDirectCheckout) {
      if (directCheckoutStartedRef.current) return;
      directCheckoutStartedRef.current = true;

      // Validate email and phone before proceeding with direct checkout
      const isEmailValid = validateEmail(email);
      const isPhoneValid = validatePhone(phone);

      if (!isEmailValid) {
        showErrorToast("Invalid email address provided in URL. Please check the email format.");
        setLoading(false);
        return;
      }

      if (!isPhoneValid) {
        showErrorToast(
          `Invalid phone number provided in URL. ${phoneValidationMessage}.`
        );
        setLoading(false);
        return;
      }

      console.log("All details present and valid, initiating direct checkout");
      // Go straight to checkout with all details
      // Get productId from URL params OR from linkDetails (if link was created with key/token)
      const finalProductId = productId || linkDetails?.idProduct?._id || linkDetails?.idProduct || linkDetails?.idSubscription?._id || linkDetails?.idSubscription;
      const directCheckoutPayload = {
        email: email,
        phone: phone,
        firstName: firstName,
        lastName: lastName,
        productId: finalProductId,
        rrp:
          pricingLocked
            ? undefined
            : resolveCheckoutRrpForVehicle(
                vehicleTypeFromUrl || formData.vehicleType,
              ) ??
              (rrp != null && rrp !== "" ? rrp : linkDetails?.rrp ?? null),
        partnerId: finalPartnerId,
        partnerUserId: finalPartnerUserId,
        idSignUpLink: linkDetails._id,
        ...(customerId ? { customerId } : {}),
      };

      goToCheckoutDirectly(directCheckoutPayload);
    }
  }, [linkDetails]); // Only run when linkDetails changes

  const activeProductId = resolveActiveProductId();
  const selectedVehicleType = formData.vehicleType || vehicleTypeFromUrl || null;
  const checkoutRrp = resolveCheckoutRrpForVehicle(selectedVehicleType);
  const selectedMonthlyPrice = (() => {
    if (hasCcwSiteContext) {
      const ccwMonthly = resolveCcwMonthlyPrice(
        activeProductId,
        selectedVehicleType,
        productInfo || getActiveProduct()
      );
      if (ccwMonthly != null) return ccwMonthly;
    }
    if (checkoutRrp != null) return checkoutRrp / 12;
    return null;
  })();
  const showVariantPricing =
    hasCcwSiteContext &&
    productSupportsVehicleTypeSelection(
      activeProductId,
      productInfo || getActiveProduct()
    );
  const showLockedVariantSummary = Boolean(
    lockedVehicleType || (pricingLocked && selectedMonthlyPrice != null)
  );

  const effectiveLockInMonths =
    resolvedLockInMonths ??
    (lockInPeriodMonthsFromUrl ? Number(lockInPeriodMonthsFromUrl) : null);
  const effectiveProductInterval =
    billingInterval ||
    linkDetails?.interval ||
    productInfo?.interval ||
    null;
  const billingLabel = resolveSignupBillingLabel(
    effectiveProductInterval,
    effectiveLockInMonths
  );

  const renderCartBillingSuffix = () => {
    if (!billingLabel) return null;
    return (
      <span className="text-gray-600 text-sm ml-1">
        (Billed {billingLabel})
      </span>
    );
  };

  return (
    <div
      className={`flex flex-col bg-gray-100 ${
        isLp ? "min-h-0" : "min-h-screen"
      }`}
    >
      {!isLp && (
        <header className="flex justify-end items-center px-6 py-4 bg-white shadow-md">
          <a href="/login" className="text-blue-500 font-semibold text-lg">
            Member Login
          </a>
        </header>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="flex flex-col md:flex-row w-full max-w-5xl mx-auto mt-6">
          <div className="w-full md:w-full bg-white p-8 shadow-md rounded-lg">
            <p>Loading...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col md:flex-row w-full max-w-5xl mx-auto mt-6">
          <div className="w-full md:w-full bg-white p-8 shadow-md rounded-lg">
            <p className="text-red-600 text-base leading-relaxed">{error}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row w-full max-w-5xl mx-auto mt-6">
          <div className="w-full md:w-full bg-white p-8 shadow-md rounded-lg">
            <h2 className="text-xl font-bold mb-4">
              {isCoffeePass ? "Coffee Pass Sign-up" : "Customer Self Sign-up"}
            </h2>

            {linkDetails ? (
              <>
                <p className="text-gray-600 mb-6">
                  {isCoffeePass
                    ? "Welcome to the Priority One Coffee Pass. Complete the form below and we'll do the rest!"
                    : "Welcome to Priority One Wash Club Sign-up. Your dealer has pre-filled some information. Complete the form below and we'll do the rest!"}
                </p>

                {/* Dealer Info */}
                <div className="mb-6">
                  <p>
                    <strong>Your Cart:</strong>{" "}
                    {formatMembershipProductDisplayName(
                      linkDetails?.idProduct
                        ? linkDetails.idProduct?.name
                        : linkDetails?.idSubscription?.name || "N/A",
                      { lockInPeriodMonths: effectiveLockInMonths }
                    )}
                    {renderCartBillingSuffix()}
                  </p>
                  {showVariantPricing && selectedMonthlyPrice != null && (
                    <p>
                      <strong>Price:</strong> {formatMonthlyPrice(selectedMonthlyPrice)}
                    </p>
                  )}
                  {showLockedVariantSummary && (
                    <>
                      {lockedVehicleType && (
                        <p>
                          <strong>Vehicle type:</strong>{" "}
                          {vehicleTypeLabels[lockedVehicleType] || lockedVehicleType}
                        </p>
                      )}
                      {selectedMonthlyPrice != null && !showVariantPricing && (
                        <p>
                          <strong>Price:</strong>{" "}
                          {formatMonthlyPrice(selectedMonthlyPrice)}
                        </p>
                      )}
                    </>
                  )}
                  <p>
                    <strong>Your Dealer:</strong> {linkDetails?.idPartnerCompany?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Consultant Name:</strong> {linkDetails?.idUser?.firstName || "N/A"}{" "}
                    {linkDetails?.idUser?.lastName || ""}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  {isCoffeePass
                    ? "Welcome to the Priority One Coffee Pass. Complete the form below and we'll do the rest!"
                    : "Welcome to Priority One Wash Club Sign-up. Complete the form below and we'll do the rest!"}
                </p>

                {/* Product Info from search params or fallback */}
                <div className="mb-6">
                  {productInfo ? (
                    <>
                      <p>
                        <strong>Your Cart:</strong>{" "}
                        {formatMembershipProductDisplayName(
                          productInfo.name || "N/A",
                          { lockInPeriodMonths: effectiveLockInMonths }
                        )}
                        {renderCartBillingSuffix()}
                      </p>
                      {productInfo.description && (
                        <p className="text-gray-600 text-sm">{productInfo.description}</p>
                      )}
                      {showVariantPricing && selectedMonthlyPrice != null && (
                        <p>
                          <strong>Price:</strong> {formatMonthlyPrice(selectedMonthlyPrice)}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Product ID:</strong> {productId || "N/A"}
                      </p>
                      {rrp && (
                        <p>
                          <strong>RRP:</strong> ${parseFloat(rrp).toFixed(2)}
                        </p>
                      )}
                    </>
                  )}
                  {partnerId && (
                    <p>
                      <strong>Partner ID:</strong> {partnerId}
                    </p>
                  )}
                  {partnerUserId && (
                    <p>
                      <strong>Partner User ID:</strong> {partnerUserId}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Form Fields */}
            <div className="flex flex-col gap-4">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email Address*"
                className="border rounded px-3 py-2 w-full"
                required
              />
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={
                  isNzRegion
                    ? "Your Mobile Number* (e.g. 0212345678)"
                    : "Your Mobile Number*"
                }
                className="border rounded px-3 py-2 w-full"
                required
              />
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Your First Name*"
                className="border rounded px-3 py-2 w-full"
                required
              />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Your Last Name*"
                className="border rounded px-3 py-2 w-full"
                required
              />
              {/* Vehicle type — only when product has variant pricing (e.g. Sparkle) */}
              {showVehicleTypeSelector && (
                <div>
                  <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle type *
                  </label>
                  <select
                    id="vehicleType"
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                    className="border rounded px-3 py-2 w-full"
                    required
                  >
                    <option value="">Select your vehicle type</option>
                    <option value="sedan">Sedan / Hatch</option>
                    <option value="wagon">Wagon / SUV</option>
                    <option value="4wd">4WD / 7 Seater</option>
                  </select>
                </div>
              )}
              {lockedVehicleType && !showVehicleTypeSelector && !isCoffeePass && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle type
                  </label>
                  <input
                    type="text"
                    className="border rounded px-3 py-2 w-full bg-gray-100 text-gray-700"
                    value={vehicleTypeLabels[lockedVehicleType] || lockedVehicleType}
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This offer is locked to this vehicle type.
                  </p>
                </div>
              )}
              {/* Terms & Conditions */}
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  className="mr-2"
                />
                I agree to Priority One Wash Club{" "}
                <a
                  href="https://priorityonewashclub.com/terms-and-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  terms and conditions
                </a>
                .
              </label>

              {/* Checkout Button */}
              <button
                type="button"
                onClick={goToCheckout}
                disabled={checkoutInProgress || loading}
                className="bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {checkoutInProgress ? "Redirecting to checkout..." : "Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}
      {identityModalOpen && identityModalPayload && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-labelledby="identity-conflict-title"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 id="identity-conflict-title" className="text-lg font-semibold text-gray-900 mb-2">
              Account details already in use
            </h3>
            <p className="text-sm text-gray-700 mb-3">{identityModalPayload.message}</p>
            {identityModalPayload.data?.conflictType === "email" &&
              identityModalPayload.data?.existingPhone &&
              identityModalPayload.data?.requestedPhone && (
                <p className="text-xs text-gray-600 mb-3">
                  On file: <strong>{identityModalPayload.data.existingPhone}</strong> — you entered:{" "}
                  <strong>{identityModalPayload.data.requestedPhone}</strong>
                </p>
              )}
            {identityModalPayload.data?.conflictType === "phone" &&
              identityModalPayload.data?.existingEmail &&
              identityModalPayload.data?.requestedEmail && (
                <p className="text-xs text-gray-600 mb-3">
                  On file: <strong>{identityModalPayload.data.existingEmail}</strong> — you entered:{" "}
                  <strong>{identityModalPayload.data.requestedEmail}</strong>
                </p>
              )}
            <p className="text-xs text-gray-500 mb-4">
              <strong>Merge</strong> updates the existing Priority One account to match what you entered.{" "}
              <strong>Create separate customer</strong> keeps the other account unchanged and starts a new one (you will
              still pay on the next step).
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                onClick={() => {
                  setIdentityModalOpen(false);
                  setPendingCheckoutPayload(null);
                  setIdentityModalPayload(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-blue-600 text-blue-700 rounded-lg"
                onClick={() => resolveIdentityAndCheckout("create_new")}
              >
                Create separate customer
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                onClick={() => resolveIdentityAndCheckout("merge")}
              >
                Merge into existing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfSignUpRegister;
